import type { ReactNode } from "react";
import { HintTooltip } from "./HintTooltip";

export type MetricTone =
  | "neutral"
  | "sky"
  | "emerald"
  | "amber"
  | "red"
  | "slate";

interface MetricKpiTileProps {
  label: string;
  value: ReactNode;
  delta?: ReactNode;
  tone?: MetricTone;
  hint?: string;
  size?: "md" | "sm";
  isLoading?: boolean;
  errorTitle?: string;
}

const TONE_CONTAINER: Record<MetricTone, string> = {
  neutral: "border-border-subtle bg-surface-inset/60",
  sky: "border-info/40 bg-info/10 dark:shadow-[0_0_24px_-8px_rgba(56,189,248,0.55)]",
  emerald:
    "border-success/40 bg-success/10 dark:shadow-[0_0_24px_-8px_rgba(16,185,129,0.55)]",
  amber:
    "border-warning/40 bg-warning/10 dark:shadow-[0_0_24px_-8px_rgba(245,158,11,0.5)]",
  red: "border-danger/40 bg-danger/10 dark:shadow-[0_0_24px_-8px_rgba(239,68,68,0.5)]",
  slate: "border-border-strong bg-surface-card/60",
};

const TONE_LABEL: Record<MetricTone, string> = {
  neutral: "text-fg-muted",
  sky: "text-info-text/80",
  emerald: "text-success-text/80",
  amber: "text-warning-text/80",
  red: "text-danger-text/80",
  slate: "text-fg-muted",
};

const TONE_VALUE: Record<MetricTone, string> = {
  neutral: "text-fg-primary",
  sky: "text-info-text-strong",
  emerald: "text-success-text-strong",
  amber: "text-warning-text-strong",
  red: "text-danger-text-strong",
  slate: "text-fg-secondary",
};

export function MetricKpiTile({
  label,
  value,
  delta,
  tone = "neutral",
  hint,
  size = "md",
  isLoading = false,
  errorTitle,
}: MetricKpiTileProps) {
  const containerTone = errorTitle ? TONE_CONTAINER.red : TONE_CONTAINER[tone];
  const labelTone = errorTitle ? TONE_LABEL.red : TONE_LABEL[tone];
  const valueTone = errorTitle ? TONE_VALUE.red : TONE_VALUE[tone];
  const valueSize = size === "sm" ? "text-xl" : "text-3xl";

  return (
    <div
      className={`rounded-xl border px-4 py-4 transition-colors ${containerTone}`}
    >
      <div
        className={`flex items-center justify-between gap-2 text-[11px] font-medium uppercase tracking-wide ${labelTone}`}
      >
        <span className="truncate">{label}</span>
        {hint && <HintTooltip text={hint} />}
      </div>

      <div
        className={`mt-2 font-semibold tabular-nums ${valueSize} ${valueTone}`}
      >
        {isLoading ? (
          <span className="inline-block h-8 w-24 animate-pulse rounded bg-surface-skeleton" />
        ) : errorTitle ? (
          <span className="text-base font-medium">Error</span>
        ) : (
          value
        )}
      </div>

      <div className="mt-1 min-h-[1rem] text-[11px] text-fg-faint">
        {isLoading ? (
          <span className="inline-block h-2 w-32 animate-pulse rounded bg-surface-skeleton/80" />
        ) : errorTitle ? (
          <span className="text-danger-text">{errorTitle}</span>
        ) : (
          delta ?? <span aria-hidden="true">&nbsp;</span>
        )}
      </div>
    </div>
  );
}
